import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import isFinite from 'lodash/isFinite';
import round from 'lodash/round';
import Accordion from '../../../../../../components/LazyWidgets/Accordion';
import Table from '../../../../../../components/LazyWidgets/Table';
import { REPORT_TYPE_INSIGHTS_LO } from '../../../../../actions/constants';
import QQInsightLOAssignments from '../QQInsightLOAssignments';
import QQBarSection from '../QQBarSection';
import QQLegend from '../QQLegend';
import style from './QQBarAndLabel.module.css';

const { AccordionBody, AccordionItem, AccordionTitle } = Accordion;

const QQBarAndLabel = ({
  isDetailView,
  contentExpanded,
  courseId,
  insightData,
  reportType,
  sendAnalyticsEvent,
}) => {
  const {
    name = '',
    assignments,
    learningObjectives = [],
    correctOnFirstTry,
    eventuallyCorrect,
    questions = [],
    incorrect,
    unanswered,
  } = insightData;

  const accordionId = name.toUpperCase();
  const detailClass = isDetailView ? style.detailView : '';
  const isLOReport = reportType === REPORT_TYPE_INSIGHTS_LO;
  let loAssignments = [];

  if (isLOReport) {
    loAssignments = assignments;
  } else {
    loAssignments = learningObjectives.length > 0 ? learningObjectives.map(lo => ({
      ...insightData,
      name: lo.name,
    })) :
    loAssignments = [{
      ...insightData,
      name: null,
    }];
  }

  const Title = ({ children }) => (<div className={style.qqBarAndLabelTitle}>{children}</div>);

  Title.propTypes = {
    children: PropTypes.string,
  };

  const Bars = () => (
    <div className={[style.qqBarAndLabelBarContainer, style.detailView].join(' ')}>
      {isFinite(correctOnFirstTry) &&
        <QQBarSection
          color="blue"
          value={correctOnFirstTry}
          label={`${round(correctOnFirstTry)}% of questions were correct first try`}
        />
      }
      {isFinite(eventuallyCorrect) &&
        <QQBarSection
          color="lightBlue"
          value={eventuallyCorrect}
          label={`${round(eventuallyCorrect)}% of questions were eventually correct`}
        />
      }
      {isFinite(incorrect) &&
        <QQBarSection
          color="red"
          value={incorrect}
          label={`${round(incorrect)}% of questions were incorrect`}
        />
      }
      {isFinite(unanswered) &&
        <QQBarSection
          color="gray"
          value={unanswered}
          label={`${round(unanswered)}% of questions never answered`}
        />
      }
    </div>
  );

  const QuestionLevelTable = ({ className }) => {
    const questionLevelData = questions.map((q) => {
      const {
        name: question,
        correctOnFirstTry: firstAmount,
        eventuallyCorrect: eventualAmount,
        incorrect: incorrectAmount,
        numQuestions,
      } = q;

      const first = `${round((firstAmount / numQuestions) * 100, 2)}%`;
      const eventual = `${round((eventualAmount / numQuestions) * 100, 2)}%`;
      const never = `${round((incorrectAmount / numQuestions) * 100, 2)}%`;

      return {
        question,
        first,
        eventual,
        never,
      };
    });

    const hasQuestions = questionLevelData.length > 0;
    const columns = [
      { accessor: 'question', Header: 'Lowest performing questions' },
      { accessor: 'first', Header: 'First try correct' },
      { accessor: 'eventual', Header: 'Eventually Correct' },
      { accessor: 'never', Header: 'Never Correct' },
    ];

    const styling = [{ row: 'styledRow', backgroundColor: 'rgba(255,255,255,1)', color: null }];

    return (
      hasQuestions && <div className={style.QuestionLevelTable}>
        <Table
          className={className}
          data={questionLevelData}
          columns={columns}
          styling={styling}
          compact
          onSortedChange={() => {}}
          autosort={false}
        />
      </div>
    );
  };

  QuestionLevelTable.propTypes = {
    className: PropTypes.string,
  };

  const OverviewContent = ({ show = true, title }) => (
    <Fragment>
      {show && title && <Title>{title}</Title>}
      {show && <Bars />}
    </Fragment>
  );

  OverviewContent.propTypes = {
    show: PropTypes.bool,
    title: PropTypes.string,
  };

  const DetailContent = ({ title }) => (
    <Fragment>
      <Accordion
        className={style.GBAccordion}
        mode="single"
        selectedItemsIndex={contentExpanded ? [0] : []}
        onChange={() => {}}
      >
        <AccordionItem id={`accordion_item_${accordionId}`}>
          <AccordionTitle
            id={`accordion_title_${accordionId}`}
            onKeyDown={() => {}}
            onClick={() => {}}
            iconClosed={{ type: 'chevron_right', fill: '#080808' }}
            iconOpen={{ type: 'chevron_down', fill: '#080808' }}
          >
            {title}
          </AccordionTitle>
          <AccordionBody className={[style.GBAccordion, style.accordionBody].join(' ')} id={`accordion_body_${accordionId}`}>
            <OverviewContent show={isDetailView} />
            <QQLegend isDetailView />
            <QQInsightLOAssignments
              courseId={courseId}
              loAssignments={loAssignments}
              reportType={reportType}
              sendAnalyticsEvent={sendAnalyticsEvent}
            />
            <QuestionLevelTable className={style.detailContent} />
          </AccordionBody>
        </AccordionItem>
      </Accordion>
    </Fragment>
  );

  DetailContent.propTypes = {
    title: PropTypes.string,
  };

  return (
    <Fragment>
      {!isDetailView && <div className={`${style.qqBarAndLabelWrapper} ${detailClass}`}>
        <OverviewContent title={name} show />
      </div>}
      {isDetailView && <div className={`${style.qqBarAndLabelWrapper} ${detailClass}`}>
        <DetailContent title={name} />
      </div>}
    </Fragment>
  );
};

QQBarAndLabel.propTypes = {
  contentExpanded: PropTypes.bool,
  isDetailView: PropTypes.bool,
  courseId: PropTypes.string,
  reportType: PropTypes.string,
  insightData: PropTypes.object,
  sendAnalyticsEvent: PropTypes.func,
};

export default QQBarAndLabel;
